import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Input, Select, Space, Tabs, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useNotify } from '@/hooks/useNotify';
import { useGetSeoSettingsQuery, useUpdateSeoSettingsMutation } from '@/modules/seo/seoApi';
import type { SeoImageAlt, SeoPageSetting, SeoRedirect, SiteSeoSettings } from '@/types/seo';
import { resolveMutationResult } from '@/utils/apiResponse';

function lines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function joinLines(values: string[] | undefined): string {
  return (values ?? []).join('\n');
}

function joinKeywords(values: string[] | undefined): string {
  return (values ?? []).join(', ');
}

function splitKeywords(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

const emptyPage = (): SeoPageSetting => ({
  path: '/',
  metaTitle: '',
  metaDescription: '',
  metaKeywords: [],
  robots: 'index, follow',
  canonical: '',
});

export default function SeoSettings() {
  const notify = useNotify();
  const { data, isLoading } = useGetSeoSettingsQuery();
  const [updateSeo, { isLoading: isSaving }] = useUpdateSeoSettingsMutation();
  const [form, setForm] = useState<SiteSeoSettings | null>(null);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const patch = (next: Partial<SiteSeoSettings>) => {
    setForm((current) => (current ? { ...current, ...next } : current));
  };

  const save = async () => {
    if (!form) return;
    const result = await resolveMutationResult(updateSeo(form));
    if (result.ok) notify.success('SEO settings saved');
    else notify.error('Could not save SEO settings', result.message);
  };

  if (!form) {
    return (
      <>
        <PageHeader />
        <Card loading={isLoading}>Loading SEO settings…</Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        actions={
          <Button type="primary" loading={isSaving} onClick={() => void save()}>
            Save SEO
          </Button>
        }
      />
      <Card>
        <Tabs
          items={[
            {
              key: 'meta',
              label: 'Meta tags',
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Typography.Text strong>Default meta title</Typography.Text>
                    <Input
                      value={form.defaultMetaTitle}
                      onChange={(event) => patch({ defaultMetaTitle: event.target.value })}
                    />
                  </div>
                  <div>
                    <Typography.Text strong>Default meta description</Typography.Text>
                    <Input.TextArea
                      rows={3}
                      value={form.defaultMetaDescription}
                      onChange={(event) => patch({ defaultMetaDescription: event.target.value })}
                    />
                  </div>
                  <div>
                    <Typography.Text strong>Default keywords</Typography.Text>
                    <Input
                      value={joinKeywords(form.defaultMetaKeywords)}
                      onChange={(event) =>
                        patch({ defaultMetaKeywords: splitKeywords(event.target.value) })
                      }
                      placeholder="Comma separated"
                    />
                  </div>
                  <div>
                    <Typography.Text strong>Canonical site URL</Typography.Text>
                    <Input
                      value={form.canonicalBaseUrl}
                      onChange={(event) => patch({ canonicalBaseUrl: event.target.value })}
                      placeholder="https://www.indiastatpublications.com"
                    />
                  </div>
                  <Typography.Paragraph type="secondary">
                    Book pages still use the SEO tab on each product. These defaults cover the rest
                    of the site. Add a page row when a specific URL needs its own tags.
                  </Typography.Paragraph>
                  {form.pages.map((page, index) => (
                    <Card
                      key={`${page.path}-${index}`}
                      size="small"
                      extra={
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() =>
                            patch({ pages: form.pages.filter((_, row) => row !== index) })
                          }
                        />
                      }
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Input
                          addonBefore="Path"
                          value={page.path}
                          onChange={(event) => {
                            const pages = [...form.pages];
                            pages[index] = { ...page, path: event.target.value };
                            patch({ pages });
                          }}
                        />
                        <Input
                          addonBefore="Title"
                          value={page.metaTitle}
                          onChange={(event) => {
                            const pages = [...form.pages];
                            pages[index] = { ...page, metaTitle: event.target.value };
                            patch({ pages });
                          }}
                        />
                        <Input.TextArea
                          rows={2}
                          value={page.metaDescription}
                          placeholder="Meta description"
                          onChange={(event) => {
                            const pages = [...form.pages];
                            pages[index] = { ...page, metaDescription: event.target.value };
                            patch({ pages });
                          }}
                        />
                        <Input
                          addonBefore="Keywords"
                          value={joinKeywords(page.metaKeywords)}
                          onChange={(event) => {
                            const pages = [...form.pages];
                            pages[index] = {
                              ...page,
                              metaKeywords: splitKeywords(event.target.value),
                            };
                            patch({ pages });
                          }}
                        />
                        <Input
                          addonBefore="Robots"
                          value={page.robots}
                          placeholder="index, follow"
                          onChange={(event) => {
                            const pages = [...form.pages];
                            pages[index] = { ...page, robots: event.target.value };
                            patch({ pages });
                          }}
                        />
                        <Input
                          addonBefore="Canonical"
                          value={page.canonical}
                          placeholder="https://www.indiastatpublications.com/"
                          onChange={(event) => {
                            const pages = [...form.pages];
                            pages[index] = { ...page, canonical: event.target.value };
                            patch({ pages });
                          }}
                        />
                      </Space>
                    </Card>
                  ))}
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => patch({ pages: [...form.pages, emptyPage()] })}
                  >
                    Add page
                  </Button>
                </Space>
              ),
            },
            {
              key: 'alts',
              label: 'Alt tags',
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Typography.Paragraph type="secondary">
                    Image key is the image path, or a book slug. The matching picture uses this alt
                    text.
                  </Typography.Paragraph>
                  {form.imageAlts.map((row, index) => (
                    <Space key={`${row.imageKey}-${index}`} align="start" style={{ width: '100%' }}>
                      <Input
                        style={{ width: 360 }}
                        value={row.imageKey}
                        placeholder="/assets/images/example.png or book-slug"
                        onChange={(event) => {
                          const imageAlts = [...form.imageAlts];
                          imageAlts[index] = { ...row, imageKey: event.target.value };
                          patch({ imageAlts });
                        }}
                      />
                      <Input
                        style={{ width: 420 }}
                        value={row.alt}
                        placeholder="Alt text"
                        onChange={(event) => {
                          const imageAlts = [...form.imageAlts];
                          imageAlts[index] = { ...row, alt: event.target.value };
                          patch({ imageAlts });
                        }}
                      />
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          patch({
                            imageAlts: form.imageAlts.filter((_, rowIndex) => rowIndex !== index),
                          })
                        }
                      />
                    </Space>
                  ))}
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() =>
                      patch({
                        imageAlts: [...form.imageAlts, { imageKey: '', alt: '' } satisfies SeoImageAlt],
                      })
                    }
                  >
                    Add alt tag
                  </Button>
                </Space>
              ),
            },
            {
              key: 'robots',
              label: 'Robots & sitemap',
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Typography.Text strong>robots.txt allow</Typography.Text>
                    <Input.TextArea
                      rows={4}
                      value={joinLines(form.robotsAllow)}
                      onChange={(event) => patch({ robotsAllow: lines(event.target.value) })}
                    />
                  </div>
                  <div>
                    <Typography.Text strong>robots.txt disallow</Typography.Text>
                    <Input.TextArea
                      rows={4}
                      value={joinLines(form.robotsDisallow)}
                      onChange={(event) => patch({ robotsDisallow: lines(event.target.value) })}
                    />
                  </div>
                  <div>
                    <Typography.Text strong>Sitemap paths</Typography.Text>
                    <Typography.Paragraph type="secondary">
                      One path per line. These become the URLs in sitemap.xml.
                    </Typography.Paragraph>
                    <Input.TextArea
                      rows={10}
                      value={joinLines(form.sitemapPaths)}
                      onChange={(event) => patch({ sitemapPaths: lines(event.target.value) })}
                    />
                  </div>
                </Space>
              ),
            },
            {
              key: 'redirects',
              label: '301 redirects',
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Typography.Paragraph type="secondary">
                    Old path to new path. Example: /indian_Electoral_Democracy → /product/your-book/
                  </Typography.Paragraph>
                  {form.redirects.map((row, index) => (
                    <Space key={`${row.fromPath}-${index}`} wrap>
                      <Input
                        style={{ width: 280 }}
                        value={row.fromPath}
                        placeholder="/old-path"
                        onChange={(event) => {
                          const redirects = [...form.redirects];
                          redirects[index] = { ...row, fromPath: event.target.value };
                          patch({ redirects });
                        }}
                      />
                      <Input
                        style={{ width: 360 }}
                        value={row.toPath}
                        placeholder="/new-path"
                        onChange={(event) => {
                          const redirects = [...form.redirects];
                          redirects[index] = { ...row, toPath: event.target.value };
                          patch({ redirects });
                        }}
                      />
                      <Select
                        style={{ width: 110 }}
                        value={row.statusCode}
                        options={[
                          { value: 301, label: '301' },
                          { value: 302, label: '302' },
                        ]}
                        onChange={(statusCode) => {
                          const redirects = [...form.redirects];
                          redirects[index] = { ...row, statusCode };
                          patch({ redirects });
                        }}
                      />
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          patch({
                            redirects: form.redirects.filter((_, rowIndex) => rowIndex !== index),
                          })
                        }
                      />
                    </Space>
                  ))}
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() =>
                      patch({
                        redirects: [
                          ...form.redirects,
                          { fromPath: '/old-path', toPath: '/', statusCode: 301 } satisfies SeoRedirect,
                        ],
                      })
                    }
                  >
                    Add redirect
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
